// ── 6.1-4. What the session knows about itself ─────────────────────
//
// Three rules that were decided by reading two exports side by side and
// comparing timestamps by hand. They live here rather than inside the
// component so they can be checked rather than described.

// Whether the hand that made an arrangement had already seen a reading.
//
// This is the state of the SESSION at the freeze, not of the arrangement. The
// record for a first reading says "pre-reading" even though the reading is
// about to exist, which is the useful reading of it: the arrangement was made
// by someone who had not been told anything yet.
//
// The prepared example must not flip this. It puts a reading on the screen
// without the visitor asking for one, and a canvas arranged after looking at
// the seed is still a canvas whose own arrangement has not been read.
export const phaseAt = (readingSeen) => (readingSeen ? "post-reading" : "pre-reading");

// Whether freeze & read has anything left to do.
//
// Session 01 froze one arrangement five times inside seventeen seconds. Four of
// those were cache hits returning the reading already on the screen; the button
// simply gave no sign that pressing it again could not produce anything new.
//
// gen > 0 is what keeps the prepared example readable. With a seeded canvas
// there is a pair on screen and nothing has moved, and if that were enough to
// sleep the button the first visitor could never ask for anything.
export const isAsleep = ({ gen, hasPair, frozenChanged }) =>
  gen > 0 && !!hasPair && !frozenChanged;

// The hand's edits since the last reading was committed.
//
// The arrangement says where things ended up. This says what happened to get
// there, which is the difference between "they moved sunburn" and "sunburn is
// somewhere else now" — and after a reading is on screen, that difference is
// the whole question.
//
// It resets at each committed reading rather than accumulating, so a record
// carries what changed since the previous one and the phase label applies to
// every edit under it uniformly.
export const EDIT_CAP = 500;

export function createEditLog(cap = EDIT_CAP) {
  let items = [];
  let truncated = false;
  return {
    // `now` is injected so a test can assert ordering without sleeping, and so
    // nothing here reaches for a clock the caller cannot see.
    note(kind, id, extra = {}, now = () => new Date().toISOString()) {
      if (items.length >= cap) { truncated = true; return false; }
      items.push({ at: now(), kind, id, ...extra });
      return true;
    },
    // A copy. The caller holds this while an await runs, and the live log keeps
    // collecting into the next reading's record.
    take: () => items.slice(),
    truncated: () => truncated,
    reset() { items = []; truncated = false; },
    get length() { return items.length; },
  };
}
